import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReact, faJs, faNodeJs, faPython,
  faGitAlt, faAws, faGoogle, faMicrosoft,
  faDocker, faLinux,
} from "@fortawesome/free-brands-svg-icons";
import {
  faCode, faServer, faDatabase, faRocket,
  faProjectDiagram, faSyncAlt, faFire, faLeaf, faWind,
} from "@fortawesome/free-solid-svg-icons";

const ICON_MAP = {
  react: faReact, js: faJs, "node-js": faNodeJs, python: faPython,
  "git-alt": faGitAlt, aws: faAws, google: faGoogle, microsoft: faMicrosoft,
  docker: faDocker, linux: faLinux, code: faCode, server: faServer,
  database: faDatabase, rocket: faRocket, "project-diagram": faProjectDiagram,
  "sync-alt": faSyncAlt, fire: faFire, leaf: faLeaf, wind: faWind,
};

const SKILLS = {
  "Frontend": [
    { name: "React", icon: "react", color: "#61DAFB" },
    { name: "Next.js", icon: "code", color: "#ffffff" },
    { name: "JavaScript", icon: "js", color: "#F7DF1E" },
    { name: "TypeScript", icon: "code", color: "#3178C6" },
    { name: "Tailwind CSS", icon: "wind", color: "#38BDF8" },
  ],
  "Backend": [
    { name: "Node.js", icon: "node-js", color: "#68A063" },
    { name: "Python", icon: "python", color: "#3776AB" },
    { name: "FastAPI", icon: "rocket", color: "#009688" },
    { name: "GraphQL", icon: "project-diagram", color: "#E10098" },
    { name: "REST APIs", icon: "server", color: "#258ACB" },
  ],
  "Cloud & DevOps": [
    { name: "AWS", icon: "aws", color: "#FF9900" },
    { name: "Azure", icon: "microsoft", color: "#0089D6" },
    { name: "GCP", icon: "google", color: "#4285F4" },
    { name: "Docker", icon: "docker", color: "#2496ED" },
    { name: "Kubernetes", icon: "sync-alt", color: "#326CE5" },
    { name: "Linux", icon: "linux", color: "#FCC624" },
  ],
  "AI & Data": [
    { name: "OpenAI", icon: "fire", color: "#10A37F" },
    { name: "LangChain", icon: "project-diagram", color: "#1C3C3C" },
    { name: "RAG Systems", icon: "database", color: "#258ACB" },
    { name: "MongoDB", icon: "leaf", color: "#47A248" },
    { name: "PostgreSQL", icon: "database", color: "#336791" },
  ],
  "Mobile": [
    { name: "Flutter", icon: "wind", color: "#02569B" },
    { name: "React Native", icon: "react", color: "#61DAFB" },
    { name: "iOS / Android", icon: "code", color: "#A2AAAD" },
  ],
};

// Live agent feed items — pineapples.dev style
const FEED_ITEMS = [
  { type: "cron", msg: "pipeline.run scheduled", delay: 0 },
  { type: "agent", msg: "cloud.deploy staging → production", delay: 900 },
  { type: "agent", msg: "ai.agent spawned — task: code review PR #47", delay: 1800 },
  { type: "agent", msg: "rag.query — 3 docs retrieved, answer synthesized", delay: 2700 },
  { type: "deploy", msg: "vercel.deploy ✓ production", delay: 3600 },
  { type: "agent", msg: "llm.call — drafting technical spec", delay: 4500 },
  { type: "cron", msg: "health.check 18 services · all green", delay: 5400 },
  { type: "agent", msg: "docker.build image pushed to ECR", delay: 6300 },
  { type: "deploy", msg: "k8s.rollout ✓ 3/3 pods ready", delay: 7200 },
];

function LiveFeed() {
  const [visibleLines, setVisibleLines] = useState([]);
  

  useEffect(() => {
    const show = (i) => {
      if (i >= FEED_ITEMS.length) return;
      setTimeout(() => {
        setVisibleLines(prev => [...prev.slice(-6), FEED_ITEMS[i]]);
        show(i + 1);
      }, i === 0 ? 400 : 900);
    };
    show(0);
  }, []);


  const typeColor = { cron: "#6B7280", agent: "#258ACB", deploy: "#10B981" };
  const pad = n => String(n).padStart(2, "0");
  const now = new Date();
  const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

  return (
    <div style={{
      background: "#020510", border: "1px solid rgba(37,138,203,0.2)",
      borderRadius: "14px", padding: "1.25rem 1.5rem",
      fontFamily: "'Fira Code', 'Courier New', monospace",
      fontSize: "0.78rem", lineHeight: 1.7,
      position: "relative", overflow: "hidden",
      minHeight: "220px",
    }}>
      {/* Header bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "1rem", paddingBottom: "0.75rem",
        borderBottom: "1px solid rgba(37,138,203,0.12)",
      }}>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          {["#FF5F57","#FFBD2E","#28C840"].map(c => (
            <span key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c, display: "inline-block" }} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{
            width: 7, height: 7, borderRadius: "50%", background: "#10B981",
            display: "inline-block", boxShadow: "0 0 6px #10B981",
            animation: "livePulse 1.5s infinite",
          }} />
          <span style={{ color: "#6B7280", fontSize: "0.72rem" }}>nuage · agent feed · live</span>
          <span style={{ color: "#4A5A7A", fontSize: "0.7rem", marginLeft: "0.5rem" }}>{timeStr}</span>
        </div>
      </div>

      {/* Feed lines */}
      {visibleLines.map((line, i) => (
        <div key={i} style={{
          display: "flex", gap: "0.75rem", marginBottom: "0.25rem",
          animation: "fadeSlideIn 0.4s ease",
        }}>
          <span style={{ color: "#4A5A7A", minWidth: "60px" }}>
            {pad(now.getHours())}:{pad(now.getMinutes())}:{pad(Math.max(0, now.getSeconds() - (visibleLines.length - 1 - i) * 15))}
          </span>
          <span style={{
            color: typeColor[line.type] || "#258ACB",
            minWidth: "48px", fontWeight: 600,
          }}>{line.type}</span>
          <span style={{ color: "#94A3B8" }}>{line.msg}</span>
        </div>
      ))}

      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
      `}</style>
    </div>
  );
}

const CATEGORIES = ["All", ...Object.keys(SKILLS)];

export default function Technologies() {
  const [active, setActive] = useState("All");

  const entries = active === "All"
    ? Object.entries(SKILLS)
    : Object.entries(SKILLS).filter(([cat]) => cat === active);

  return (
    <section id="technologies" style={{ padding: "7rem 0", position: "relative", overflow: "hidden" }}>
      {/* Glow */}
      <div style={{
        position: "absolute", width: "500px", height: "500px", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(37,138,203,0.07) 0%, transparent 70%)",
        bottom: "0", right: "-100px", filter: "blur(60px)", pointerEvents: "none",
      }} />

      <div className="container">
        {/* Header row */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: "4rem", alignItems: "start", marginBottom: "3.5rem",
        }} className="tech-header-grid">
          <div>
            <div className="section-label"><span>◆</span> Tech Stack</div>
            <h2 className="section-heading">
              Built on tools<br /><span className="gradient-text">we run daily</span>
            </h2>
            <p className="section-subtext" style={{ marginBottom: "1.5rem" }}>
              We don't recommend technologies from the sidelines — we ship with them in production, every day, for our own systems and yours.
            </p>
            {/* Stats row — pineapples.dev inspired */}
            <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
              {[
                { val: "18+", label: "Tech mastered" },
                { val: "5+", label: "Cloud platforms" },
                { val: "Daily", label: "In production" },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "1.6rem", fontWeight: 800, color: "var(--accent)" }}>{s.val}</div>
                  <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Live feed */}
          <LiveFeed />
        </div>

        {/* Category pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "2.5rem" }}>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActive(cat)} style={{
              padding: "0.45rem 1.1rem", borderRadius: "100px",
              fontFamily: "var(--font-body)", fontSize: "0.85rem", fontWeight: 500,
              cursor: "pointer", transition: "all 0.2s",
              background: active === cat ? "var(--accent)" : "transparent",
              color: active === cat ? "white" : "var(--text-secondary)",
              border: active === cat ? "1px solid var(--accent)" : "1px solid var(--border)",
            }}>
              {cat}
            </button>
          ))}
        </div>

        {/* Skills grid */}
        {entries.map(([category, skills]) => (
          <div key={category} style={{ marginBottom: "2.5rem" }}>
            <div style={{
              fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em",
              textTransform: "uppercase", color: "var(--text-muted)",
              marginBottom: "1rem", paddingLeft: "0.25rem",
            }}>{category}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem" }}>
              {skills.map((skill) => (
                <span key={skill.name} style={{
                  display: "inline-flex", alignItems: "center", gap: "0.5rem",
                  padding: "0.5rem 1.1rem",
                  background: "rgba(37,138,203,0.05)",
                  border: "1px solid rgba(37,138,203,0.15)",
                  borderRadius: "100px",
                  fontSize: "0.88rem", fontWeight: 500,
                  color: "var(--text-primary)",
                  transition: "all 0.25s",
                  cursor: "default",
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = skill.color;
                    e.currentTarget.style.background = `${skill.color}14`;
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = `0 6px 20px ${skill.color}28`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "rgba(37,138,203,0.15)";
                    e.currentTarget.style.background = "rgba(37,138,203,0.05)";
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <FontAwesomeIcon
                    icon={ICON_MAP[skill.icon] || faCode}
                    style={{ color: skill.color, fontSize: "1rem" }}
                  />
                  <span>{skill.name}</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @media (max-width: 900px) {
          .tech-header-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}